import React, { useState } from 'react'
import Head from 'next/head'
import Header from '../header'
import Footer from '../footer'
import styles from "../../styles/BmcControlM.module.css"
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next';
import { GetStaticProps, InferGetStaticPropsType } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { IMAGE_DELIVERY } from '@/base/utils/dataHelper';
import StoneityButton from '@/components/stoneity-button/stoneityButton';
import Image from 'next/image';

enum ControlUseCasesEnum {
  Workflow,
  DataPipeline,
  FraudDetection,
  SupplyChain,
  Telecommunications,
}

type BmcControlMProps = InferGetStaticPropsType<typeof getStaticProps> & {
  setWidgetMenuOpen: (value: boolean) => void;
};

const BmcControlM = ({ setWidgetMenuOpen }: BmcControlMProps) => {
  const { t } = useTranslation("common");
  const router = useRouter();
  const [productFeaturesTab, setProductFeaturesTab] = useState(ControlUseCasesEnum.Workflow)

  const baseUrl = 'https://stoneity.com';
  const currentPath = router.asPath;
  const locale = router.locale || 'en';
  const canonicalUrl = locale === 'tr'
    ? `${baseUrl}/tr${currentPath}`
    : `${baseUrl}${currentPath}`;
  const alternateLocale = locale === 'tr' ? 'en' : 'tr';
  const alternatePath = '/bmc-control-m';

  return (
    <>
      <Head>
        <title>{t('seo_bmc_control_m_title')}</title>
        <meta name="description" content={t('seo_bmc_control_m_description')} />
        <meta name="keywords" content="BMC Control-M, iş akışı orkestrasyonu, workflow automation, data pipeline, Stoneity, enterprise orchestration" />
        <link key="canonical" rel="canonical" href={canonicalUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={t('seo_bmc_control_m_title')} />
        <meta property="og:description" content={t('seo_bmc_control_m_description')} />
        <meta property="og:image" content={`${baseUrl}/stoneityLogo.png`} />
        <meta property="og:site_name" content="Stoneity" />
        <meta property="og:locale" content={locale === 'tr' ? 'tr_TR' : 'en_US'} />
        <meta property="og:locale:alternate" content={locale === 'tr' ? 'en_US' : 'tr_TR'} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={canonicalUrl} />
        <meta name="twitter:title" content={t('seo_bmc_control_m_title')} />
        <meta name="twitter:description" content={t('seo_bmc_control_m_description')} />
        <meta name="twitter:image" content={`${baseUrl}/stoneityLogo.png`} />
        <meta name="twitter:site" content="@stoneity" />

        <link rel="alternate" hrefLang={locale} href={canonicalUrl} />
        <link rel="alternate" hrefLang={alternateLocale} href={`${baseUrl}/${alternateLocale}${alternatePath}`} />
        <link rel="alternate" hrefLang="x-default" href={`${baseUrl}${alternatePath}`} />
      </Head>
      <Header />
      <main className={styles.mainFlex}>
        <div className={styles.pageFlex}>
          <div className={styles.discoveryBannerWrapper}>
            <div className={styles.discoveryBannerLeft}>
              <div className={styles.discoveryBannerTitle}>{t("enterpriseWorkflowOrchestration")}</div>
              <div className={styles.discoveryBannerExplain}>{t("aiPoweredWorkflowOrchestration")}</div>
              <div className={styles.talkWithUsButtonWrapper}>
                <div className={styles.talkWithUsButtonImgWrapper}>
                  <img src="/human-1.png" alt="" />
                  <img src="/human-2.png" alt="" />
                  <img src="/human-3.png" alt="" />
                </div>
                <StoneityButton
                  buttonClass="talkWithUsButton"
                  buttonText={t("talkWithUs")}
                  buttonStyle="light"
                  width={200}
                  height={45}
                  onClick={() => setWidgetMenuOpen(true)}
                />
              </div>
            </div>
            <Image
              className={styles.discoveryBannerImg}
              src={`${IMAGE_DELIVERY}/2439062b-a161-487c-4d6c-1842567bf400/715x515`}
              alt=""
              width={715}
              height={515}
              priority
              sizes="(max-width: 1100px) 350px, (max-width: 1250px) 450px, 600px"
            />
          </div>
        </div>
        <div className={styles.controlCardContentFlex}>
          <div className={styles.controlCardContent}>
            <div className={styles.controlCardContentTitle}>{t("exploreControlMCapabilities")}</div>
            <div className={styles.controlCardWrapper}>
              <div className={styles.controlCard}>
                <div className={styles.controlCardTitle}>{t("businessProcessOrchestration")}</div>
                <div className={styles.controlCardExplain}>{t("businessProcessOrchestrationDescription")}</div>
              </div>
              <div className={styles.controlCard}>
                <div className={styles.controlCardTitle}>{t("aiAssistedAutomation")}</div>
                <div className={styles.controlCardExplain}>{t("aiAssistedAutomationDescription")}</div>
              </div>
              <div className={styles.controlCard}>
                <div className={styles.controlCardTitle}>{t("dataPipelineOrchestration")}</div>
                <div className={styles.controlCardExplain}>{t("dataPipelineOrchestrationDescription")}</div>
              </div>
              <div className={styles.controlCard}>
                <div className={styles.controlCardTitle}>{t("cloudAndHybridWorkflows")}</div>
                <div className={styles.controlCardExplain}>{t("cloudAndHybridWorkflowsDescription")}</div>
              </div>
              <div className={styles.controlCard}>
                <div className={styles.controlCardTitle}>{t("jobsAsCode")}</div>
                <div className={styles.controlCardExplain}>{t("jobsAsCodeDescription")}</div>
              </div>
              <div className={styles.controlCard}>
                <div className={styles.controlCardTitle}>{t("sapWorkflowOrchestration")}</div>
                <div className={styles.controlCardExplain}>{t("sapWorkflowOrchestrationDescription")}</div>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.pageFlex}>
          <div className={styles.controlTabContent}>
            <div className={styles.controlTabContentTitle}>{t("orchestrationLayerForEnterprise")}</div>
            <div className={styles.controlTabContentExplain}>{t("exploreControlM")}</div>
            <div className={styles.controlTabButtonWrapper}>
              <div className={`${styles.controlTabButton} ${productFeaturesTab === ControlUseCasesEnum.Workflow ? styles.controlTabButtonActive : ""}`} onClick={(() => { setProductFeaturesTab(ControlUseCasesEnum.Workflow) })}>{t("workflowAutomation")}</div>
              <div className={`${styles.controlTabButton} ${productFeaturesTab === ControlUseCasesEnum.DataPipeline ? styles.controlTabButtonActive : ""}`} onClick={(() => { setProductFeaturesTab(ControlUseCasesEnum.DataPipeline) })}>{t("dataPipelineOrchestration")}</div>
              <div className={`${styles.controlTabButton} ${productFeaturesTab === ControlUseCasesEnum.FraudDetection ? styles.controlTabButtonActive : ""}`} onClick={(() => { setProductFeaturesTab(ControlUseCasesEnum.FraudDetection) })}>{t("fraudDetection")}</div>
              <div className={`${styles.controlTabButton} ${productFeaturesTab === ControlUseCasesEnum.SupplyChain ? styles.controlTabButtonActive : ""}`} onClick={(() => { setProductFeaturesTab(ControlUseCasesEnum.SupplyChain) })}>{t("supplyChainOrchestration")}</div>
              <div className={`${styles.controlTabButton} ${productFeaturesTab === ControlUseCasesEnum.Telecommunications ? styles.controlTabButtonActive : ""}`} onClick={(() => { setProductFeaturesTab(ControlUseCasesEnum.Telecommunications) })}>{t("telecommunicationsWorkflows")}</div>
            </div>
            <div className={`${styles.controlTabArea} ${productFeaturesTab === ControlUseCasesEnum.Workflow ? styles.controlTabAreaActive : ""}`}>
              <img src="/control-m-workflow-automation.svg" alt="" />
              <div className={styles.controlTabAreaTextWrapper}>
                <div className={styles.controlTabAreaTitle}>{t("automateComplexBusinessWorkflows")}</div>
                <div className={styles.controlTabAreaExplain}>{t("automateComplexBusinessWorkflowsDescription")}</div>
              </div>
            </div>
            <div className={`${styles.controlTabArea} ${productFeaturesTab === ControlUseCasesEnum.DataPipeline ? styles.controlTabAreaActive : ""}`}>
              <img src="/control-m-data-pipeline.svg" alt="" />
              <div className={styles.controlTabAreaTextWrapper}>
                <div className={styles.controlTabAreaTitle}>{t("orchestrateDataPipelines")}</div>
                <div className={styles.controlTabAreaExplain}>{t("orchestrateDataPipelinesDescription")}</div>
              </div>
            </div>
            <div className={`${styles.controlTabArea} ${productFeaturesTab === ControlUseCasesEnum.FraudDetection ? styles.controlTabAreaActive : ""}`}>
              <img src="/control-m-fraud-detection.svg" alt="" />
              <div className={styles.controlTabAreaTextWrapper}>
                <div className={styles.controlTabAreaTitle}>{t("orchestrateFraudDetection")}</div>
                <div className={styles.controlTabAreaExplain}>{t("orchestrateFraudDetectionDescription")}</div>
              </div>
            </div>
            <div className={`${styles.controlTabArea} ${productFeaturesTab === ControlUseCasesEnum.SupplyChain ? styles.controlTabAreaActive : ""}`}>
              <img src="/control-m-supply-chain.svg" alt="" />
              <div className={styles.controlTabAreaTextWrapper}>
                <div className={styles.controlTabAreaTitle}>{t("keepGoodsMoving")}</div>
                <div className={styles.controlTabAreaExplain}>{t("keepGoodsMovingDescription")}</div>
              </div>
            </div>
            <div className={`${styles.controlTabArea} ${productFeaturesTab === ControlUseCasesEnum.Telecommunications ? styles.controlTabAreaActive : ""}`}>
              <img src="/control-m-telecommunications-workflows.svg" alt="" />
              <div className={styles.controlTabAreaTextWrapper}>
                <div className={styles.controlTabAreaTitle}>{t("keepNetworksRunning")}</div>
                <div className={styles.controlTabAreaExplain}>{t("keepNetworksRunningDescription")}</div>
              </div>
            </div>
          </div>
          <div className={styles.controlTabContentResponsive}>
            <div className={styles.controlTabContentFlex}>
              <div className={styles.controlTabButtonActiveResponsive}>{t("workflowAutomation")}</div>
              <div className={styles.controlTabArea}>
                <img src="/control-m-workflow-automation.svg" alt="" />
                <div className={styles.controlTabAreaTextWrapper}>
                  <div className={styles.controlTabAreaTitle}>{t("automateComplexBusinessWorkflows")}</div>
                  <div className={styles.controlTabAreaExplain}>{t("automateComplexBusinessWorkflowsDescription")}</div>
                </div>
              </div>
            </div>
            <div className={styles.controlTabContentFlex}>
              <div className={styles.controlTabButtonActiveResponsive}>{t("dataPipelineOrchestration")}</div>
              <div className={styles.controlTabArea}>
                <img src="/control-m-data-pipeline.svg" alt="" />
                <div className={styles.controlTabAreaTextWrapper}>
                  <div className={styles.controlTabAreaTitle}>{t("orchestrateDataPipelines")}</div>
                  <div className={styles.controlTabAreaExplain}>{t("orchestrateDataPipelinesDescription")}</div>
                </div>
              </div>
            </div>
            <div className={styles.controlTabContentFlex}>
              <div className={styles.controlTabButtonActiveResponsive}>{t("fraudDetection")}</div>
              <div className={styles.controlTabArea}>
                <img src="/control-m-fraud-detection.svg" alt="" />
                <div className={styles.controlTabAreaTextWrapper}>
                  <div className={styles.controlTabAreaTitle}>{t("orchestrateFraudDetection")}</div>
                  <div className={styles.controlTabAreaExplain}>{t("orchestrateFraudDetectionDescription")}</div>
                </div>
              </div>
            </div>
            <div className={styles.controlTabContentFlex}>
              <div className={styles.controlTabButtonActiveResponsive}>{t("supplyChainOrchestration")}</div>
              <div className={styles.controlTabArea}>
                <img src="/control-m-supply-chain.svg" alt="" />
                <div className={styles.controlTabAreaTextWrapper}>
                  <div className={styles.controlTabAreaTitle}>{t("keepGoodsMoving")}</div>
                  <div className={styles.controlTabAreaExplain}>{t("keepGoodsMovingDescription")}</div>
                </div>
              </div>
            </div>
            <div className={styles.controlTabContentFlex}>
              <div className={styles.controlTabButtonActiveResponsive}>{t("telecommunicationsWorkflows")}</div>
              <div className={styles.controlTabArea}>
                <img src="/control-m-telecommunications-workflows.svg" alt="" />
                <div className={styles.controlTabAreaTextWrapper}>
                  <div className={styles.controlTabAreaTitle}>{t("keepNetworksRunning")}</div>
                  <div className={styles.controlTabAreaExplain}>{t("keepNetworksRunningDescription")}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale }) => ({
  props: {
    ...(await serverSideTranslations(locale || "tr", ["common"])),
  },
});

export default BmcControlM;
